/** @jsx jsx */
import { useEffect, useState, useMemo } from 'react';
import { jsx } from '@emotion/core';
import InfiniteScroll from 'react-infinite-scroll-component';

import { IChallengeListProductItem } from 'interfaces';
import { getProductFieldId } from 'utils';
import ProductCard from '../ProductCard';
import TableSkeleton from '../TableSkeleton';
import { StyledContainer } from './styles';

const MAX_PAGINATED_PRODUCTS = 10;

function productTyposComparator(a: string, b: string): number {
  if (!a) return 1;
  if (!b) return -1;
  if (a > b) return 1;
  return -1;
}

function productsComparator(
  a: IChallengeListProductItem,
  b: IChallengeListProductItem,
) {
  if (a.challengeProduct.range !== b.challengeProduct.range) {
    return productTyposComparator(
      a.challengeProduct.range.toLowerCase(),
      b.challengeProduct.range.toLowerCase(),
    );
  }

  if (a.challengeProduct.brand !== b.challengeProduct.brand) {
    return productTyposComparator(
      a.challengeProduct.brand.toLowerCase(),
      b.challengeProduct.brand.toLowerCase(),
    );
  }

  return productTyposComparator(
    a.challengeProduct.productName.toLowerCase(),
    b.challengeProduct.productName.toLowerCase(),
  );
}

export function ProductCardsList({
  products,
  isValueFieldRequired,
}: {
  products?: IChallengeListProductItem[];
  isValueFieldRequired: boolean;
}): JSX.Element | null {
  const [sliceIndex, setSliceIndex] = useState(MAX_PAGINATED_PRODUCTS);
  const sortedProducts = useMemo(
    () => (products ? [...products].sort(productsComparator) : undefined),
    [products],
  );

  const [slicedProducts, setSlicedProducts] = useState(
    sortedProducts?.slice(0, MAX_PAGINATED_PRODUCTS) || [],
  );

  useEffect(() => {
    if (sortedProducts) {
      setSlicedProducts(sortedProducts.slice(0, sliceIndex));
    }
  }, [sortedProducts, sliceIndex]);

  function loadMoreProducts() {
    if (sortedProducts) {
      setSliceIndex(sliceIndex + MAX_PAGINATED_PRODUCTS);
    }
  }

  return (
    <InfiniteScroll
      dataLength={slicedProducts?.length}
      hasMore={!!sortedProducts && sliceIndex < sortedProducts.length}
      next={loadMoreProducts}
      loader={<TableSkeleton />}
      style={{
        paddingBottom: 8,
      }}
    >
      <StyledContainer>
        {slicedProducts?.map((product: IChallengeListProductItem) => (
          <ProductCard
            fieldId={getProductFieldId(product)}
            product={product}
            isValueFieldRequired={isValueFieldRequired}
            key={product['@id']}
          />
        ))}
      </StyledContainer>
    </InfiniteScroll>
  );
}

export default ProductCardsList;
